import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './i18n';
import App from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import axios from 'axios';
import theme, { getAppTheme } from './theme';

const THEME_CACHE_KEY = 'theme_settings';

const getInitialTheme = () => {
  try {
    const cached = localStorage.getItem(THEME_CACHE_KEY);
    if (cached) {
      return getAppTheme(JSON.parse(cached));
    }
  } catch (e) {
    console.warn('Failed to parse cached theme settings', e);
  }
  return theme;
};

const ThemeProviderWrapper = () => {
  const [currentTheme, setCurrentTheme] = useState(getInitialTheme);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const API_BASE_URL = process.env.REACT_APP_API_URL || window.location.origin;

  // Poll for token changes so theme is fetched once a token becomes available
  // (e.g. after login within the same page session)
  useEffect(() => {
    const checkToken = () => {
      const newToken = localStorage.getItem('token');
      if (newToken !== token) {
        setToken(newToken);
      }
    };
    checkToken();
    const interval = setInterval(checkToken, 500);
    return () => clearInterval(interval);
  }, [token]);

  // Fetch theme settings whenever the token becomes available
  useEffect(() => {
    if (!token) {
      // No token yet — fetch logon page settings (public endpoint) so the
      // login page itself uses the correct colors from the stored theme.
      const fetchLogonTheme = async () => {
        try {
          const res = await axios.get(`${API_BASE_URL}/api/gui/settings/logon`, { timeout: 5000 });
          setCurrentTheme(getAppTheme(res.data));
        } catch (err) {
          console.warn('Failed to fetch logon theme settings', err);
        }
      };
      fetchLogonTheme();
      return;
    }

    const fetchThemeSettings = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/gui/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const settings = res.data;
        setCurrentTheme(getAppTheme(settings));
        localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(settings));
      } catch (err) {
        console.error('Failed to fetch theme settings', err);
      }
    };

    fetchThemeSettings();
  }, [token, API_BASE_URL]);

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <App />
    </ThemeProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProviderWrapper />
  </React.StrictMode>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.register();